// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, lazy, Suspense } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import TestDataScrubber from '../components/TestDataScrubber';

// Lazy-load heavy pages to keep DevPage initial load fast
const ValuationPage = lazy(() => import('./ValuationPage'));
const PlatformAnalyticsPage = lazy(() => import('./PlatformAnalyticsPage'));
const LicenseManagerPage = lazy(() => import('./LicenseManagerPage'));

// All app Supabase projects share the same instance
// This page lets Jordan & Ryan run dev tools across all apps

const ALL_APPS = [
  { name: 'IOD (Island Overhead Doors)', url: 'islandoverheaddoors.pages.dev' },
  { name: 'CropPatrol', url: 'croppatrol.pages.dev' },
  { name: 'Forge Fleet', url: 'haulsync.pages.dev' },
  { name: 'Directional Ops', url: 'directional-ops.pages.dev' },
  { name: 'LotSync', url: 'lotsync.pages.dev' },
  { name: 'NurseVault', url: 'nursevault.pages.dev' },
  { name: 'NurseVault CA', url: 'nursevault-ca.pages.dev' },
  { name: 'Vantage Ops', url: 'vantage-ops.pages.dev' },
  { name: 'BudLedger', url: 'budledger.pages.dev' },
  { name: 'OreSync', url: 'oresync.pages.dev' },
  { name: 'VacTraq', url: 'vactraq.pages.dev' },
  { name: 'GlowDesk', url: 'glowdesk.pages.dev' },
  { name: 'ClosingDesk', url: 'closingdesk.pages.dev' },
  { name: 'WrenchSync', url: 'wrenchsync.pages.dev' },
  { name: 'TreadOps', url: 'treadops.pages.dev' },
  { name: 'HerdLedger', url: 'herdledger.pages.dev' },
  { name: 'VitaSync', url: 'vitasync.pages.dev' },
  { name: 'PolicyForge', url: 'policyforge.pages.dev' },
  { name: 'StageVault', url: 'stagevault.pages.dev' },
  { name: 'FormGenius', url: 'formgenius.pages.dev' },
  { name: 'MT Forge (Internal)', url: 'mt-forge-internal.pages.dev' },
  { name: 'IronLedger', url: 'ironledger.pages.dev' },
  { name: 'CropPatrol (Broda WL)', url: 'croppatrol-broda.pages.dev' },
  { name: 'LiftForge', url: 'hoist-control.pages.dev' },
  { name: 'RigForge', url: 'rigwarden.pages.dev' },
  { name: 'TowForge', url: 'towforge.pages.dev' },
  { name: 'FRCC', url: 'filthy-redneck-cc.pages.dev' },
  { name: 'Forge Companion', url: 'forge-companion.pages.dev' },
  { name: 'FlorigenNFT', url: 'florigen-nft.pages.dev' },
  { name: 'AisleSync', url: 'aislesync.pages.dev' },
  { name: 'PlateSync', url: 'platesync.pages.dev' },
];

export default function DevPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('tasks');
  const companyId = user?.company_id;

  const tabs = [
    { key: 'tasks', label: 'Priority Tasks', icon: '🚨' },
    { key: 'scrubber', label: 'Data Scrubber', icon: '🧹' },
    { key: 'valuation', label: 'Valuation', icon: '💰' },
    { key: 'analytics', label: 'Analytics', icon: '📊' },
    { key: 'licenses', label: 'Licenses', icon: '🔑' },
    { key: 'apps', label: 'Apps', icon: '📱' },
    { key: 'conventions', label: 'Conventions', icon: '📋' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1F2937', margin: '0 0 6px' }}>Dev Page</h1>
        <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
          Development tools for Jordan & Ryan. Test data scrubber, app directory, and dev conventions.
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #E5E7EB', paddingBottom: 0 }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '10px 18px', border: 'none', borderBottom: activeTab === tab.key ? '3px solid #3B82F6' : '3px solid transparent',
            background: activeTab === tab.key ? '#EFF6FF' : 'transparent',
            color: activeTab === tab.key ? '#1D4ED8' : '#6B7280',
            fontSize: 14, fontWeight: activeTab === tab.key ? 700 : 500,
            cursor: 'pointer', borderRadius: '8px 8px 0 0',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Priority Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 24 }}>🚨</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Priority Tasks</div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>Action items for Jordan & Ryan</div>
            </div>
          </div>

          {[
            { priority: 'HIGH', status: 'PENDING', assignee: 'Jordan', title: 'IOD Gmail / Email Setup', desc: 'Set up Google Cloud Console OAuth for info@islandoverheaddoors.com. Create project "IOD Reception Dashboard", enable Gmail API, set up OAuth consent screen, create OAuth Client ID with redirect URI: https://islandoverheaddoors.pages.dev/gmail-callback. Then go to IOD app Settings and paste the Client ID.', color: '#DC2626' },
            { priority: 'HIGH', status: 'PENDING', assignee: 'Ryan', title: 'Run Supabase Migration: license-system-migration.sql', desc: 'Execute scripts/license-system-migration.sql in Supabase SQL Editor to enable the registration key / licensing system across all apps.', color: '#DC2626' },
            { priority: 'MED', status: 'PENDING', assignee: 'Ryan', title: 'Google Workspace MX Records', desc: 'Fix @miskulintechnologies.com email routing. May conflict with Cloudflare Email Routing. Team using personal emails for now.', color: '#F59E0B' },
            { priority: 'MED', status: 'PENDING', assignee: 'Jordan', title: 'Build IOD Mobile App', desc: 'React Native / Expo mobile app for Island Overhead Doors technicians. Template available at templates/forge-app-template/.', color: '#F59E0B' },
            { priority: 'MED', status: 'DONE', assignee: 'Team', title: 'Forge Fleet Rename', desc: 'Forge Fleet rebranded to Forge Fleet. Done.', color: '#10B981' },
            { priority: 'LOW', status: 'PENDING', assignee: 'Jordan', title: 'Logos for 6 Apps', desc: 'Still missing logos for: VacTraq, GlowDesk, VitaSync, PolicyForge, StageVault, FormGenius.', color: '#3B82F6' },
            { priority: 'LOW', status: 'PENDING', assignee: 'Ryan', title: 'IOD Pitch Deck + NDA', desc: 'Create pitch deck and NDA for Island Overhead Doors. Templates available in Mt Forge/pitch-decks/.', color: '#3B82F6' },
            { priority: 'LOW', status: 'PENDING', assignee: 'Ryan', title: 'Add Google Meet Link to Meeting Emails', desc: 'meeting-prep-emails.html has 17 emails ready but needs the Google Meet link added when Ryan creates the meeting.', color: '#3B82F6' },
          ].map((task, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px',
              marginBottom: 10, borderRadius: 12, background: '#fff',
              border: `2px solid ${task.color}20`, borderLeft: `4px solid ${task.color}`,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800, color: '#fff', padding: '3px 8px',
                borderRadius: 6, background: task.color, whiteSpace: 'nowrap', marginTop: 2,
              }}>
                {task.priority}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937', marginBottom: 4 }}>
                  {task.title}
                  <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 400, marginLeft: 8 }}>
                    — {task.assignee}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.5 }}>{task.desc}</div>
              </div>
              <div style={{
                fontSize: 10, fontWeight: 700, color: task.status === 'DONE' ? '#15803D' : '#92400E',
                background: task.status === 'DONE' ? '#DCFCE7' : '#FEF3C7',
                padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap',
              }}>
                {task.status}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Test Data Scrubber Tab */}
      {activeTab === 'scrubber' && (
        <div className="card" style={{ padding: 0 }}>
          <TestDataScrubber
            supabase={supabase}
            companyId={companyId}
            onComplete={(count) => alert(`Purged ${count} test records. Refresh affected apps to see clean data.`)}
          />
        </div>
      )}

      {/* Company Valuation Tab */}
      {activeTab === 'valuation' && (
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading valuation data...</div>}>
          <ValuationPage />
        </Suspense>
      )}

      {/* Platform Analytics Tab */}
      {activeTab === 'analytics' && (
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading analytics...</div>}>
          <PlatformAnalyticsPage />
        </Suspense>
      )}

      {/* Licenses Tab */}
      {activeTab === 'licenses' && (
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading license manager...</div>}>
          <LicenseManagerPage />
        </Suspense>
      )}

      {/* App Directory Tab */}
      {activeTab === 'apps' && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>📱</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>All Deployed Apps ({ALL_APPS.length})</div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>Click any app to open it in a new tab</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {ALL_APPS.map(app => (
              <a key={app.url} href={`https://${app.url}`} target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderRadius: 10, border: '1px solid #E5E7EB', background: '#FAFAFA',
                  textDecoration: 'none', color: '#1F2937', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.borderColor = '#BFDBFE'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#3B82F6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                  {app.name.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{app.name}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>{app.url}</div>
                </div>
                <span style={{ fontSize: 14, color: '#9CA3AF' }}>↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Dev Conventions Tab */}
      {activeTab === 'conventions' && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 24 }}>📋</span>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Development Conventions</div>
          </div>

          {[
            { title: 'Test Data Tagging', content: 'When creating test data in ANY app, either prefix the name with [TEST], [DEMO], or [SAMPLE], OR set metadata.is_test = true in the record. The Test Data Scrubber detects both patterns.' },
            { title: 'Database Soft Delete', content: 'All tables use is_deleted: false by default. The scrubber sets is_deleted = true (soft delete). Records are hidden from queries but not destroyed.' },
            { title: 'Deploy Command', content: 'npx wrangler pages deploy build --project-name=[app-name] --branch=main' },
            { title: 'Build Command (Web)', content: 'cd web && npx react-scripts build' },
            { title: 'Supabase Instance', content: 'All apps share: rmoxuodrbhesecbxiwtz.supabase.co' },
            { title: 'Default Temp Password', content: 'Island2026! — used for all new team accounts' },
            { title: 'Company Emails', content: 'Capitalize first initial: J.lutz@ not j.lutz@ (Ryan\'s rule)' },
            { title: 'Proprietary Terms', content: 'Internal codenames are PROPRIETARY — never in public-facing docs or code comments' },
            { title: 'Auth Pattern', content: 'All apps: React + React Router + Supabase auth. 4-step onboarding: Password → NDA → Tutorial → Access' },
            { title: 'App Template', content: 'New apps: use templates/create-new-app.js to scaffold from forge-app-template/' },
          ].map((item, i) => (
            <div key={i} style={{
              padding: '12px 16px', marginBottom: 10, borderRadius: 10,
              background: i % 2 === 0 ? '#F9FAFB' : '#fff',
              border: '1px solid #E5E7EB',
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937', marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.5 }}>{item.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
